import { useState, useCallback, useRef } from 'react';
import { useCatalog } from '../hooks/useCatalog';
import { ProvenanceRecord } from '../contexts/CatalogContext';
import { logger } from '../utils/logger';

/**
 * A provenance record that can be edited.
 * Records from the server have recordId, new records have a temp id.
 */
export interface EditableProvenanceRecord {
  id: string; // recordId for existing, temp UUID for new
  recordId?: string; // Present only for records that exist on server
  publicRendering: string;
  internalRendering: string;
  isPublic: boolean;
  isNew?: boolean; // True for records not yet saved to server
}

/**
 * Hook for managing provenance records with a unified, editable list.
 *
 * Unlike the previous draft/committed split, ALL records are now editable
 * and can be reordered at any time. Changes are saved via PUT which replaces
 * all records atomically.
 *
 * Features:
 * - Optimistic locking via version tracking (prevents concurrent edit data loss)
 * - Error state exposed for UI feedback
 * - Automatic conflict detection with reload capability
 */
export const useCatalogProvenance = (catalogItemId?: string, ownerId?: string | null) => {
  const { getProvenanceChain, replaceProvenanceRecords, error } = useCatalog();
  const [records, setRecords] = useState<EditableProvenanceRecord[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasConflict, setHasConflict] = useState(false);
  const loadedCatalogIdRef = useRef<string | null>(null);
  const versionRef = useRef<number>(0);

  /**
   * Load provenance records from the server.
   * Converts server records to editable format and stores version for optimistic locking.
   */
  const loadProvenanceChain = useCallback(async (id?: string) => {
    const itemId = id || catalogItemId;
    if (!itemId) return [];

    setLocalLoading(true);
    setSaveError(null);
    setHasConflict(false);
    try {
      const response = await getProvenanceChain(itemId, ownerId) as { records: ProvenanceRecord[]; version: number };
      const serverRecords = Array.isArray(response) ? response : response.records || [];

      // Store version for optimistic locking (defaults to 0 for backward compatibility)
      versionRef.current = (response as { version?: number }).version ?? 0;

      // Convert server records to editable format
      const editableRecords: EditableProvenanceRecord[] = serverRecords.map(r => ({
        id: r.recordId,
        recordId: r.recordId,
        publicRendering: r.publicRendering,
        internalRendering: r.internalRendering,
        isPublic: r.isPublic,
        isNew: false,
      }));

      // Preserve any locally-added new records that have not yet been saved.
      // This prevents a race condition (#1005) where the user clicks
      // "+Add Provenance" before the in-flight GET resolves: without this
      // guard the load's setRecords([]) would wipe the new entry, causing
      // the form field to disappear before the user starts typing.
      setRecords(prevRecords => {
        const pendingNewRecords = prevRecords.filter(r => r.isNew);
        if (pendingNewRecords.length > 0) {
          // Merge: server records first (correct base state), user's unsaved
          // additions appended so they are still visible and editable.
          return [...editableRecords, ...pendingNewRecords];
        }
        return editableRecords;
      });
      loadedCatalogIdRef.current = itemId;
    } catch (err) {
      logger.error('Failed to load provenance chain', err);
      throw err;
    } finally {
      setLocalLoading(false);
    }
  }, [catalogItemId, getProvenanceChain, ownerId]);

  /**
   * Add a new record to the list. Optionally pre-fills the public rendering
   * (e.g. picking a favorite like "Artist Studio" from the Favorites rail — #1129).
   */
  const addRecord = useCallback((initialPublicRendering?: string) => {
    setRecords(prev => {
      // When pre-filling (favorite pick), reuse an existing empty "+Add" row
      // instead of appending a second record — a lingering empty isNew row
      // makes the autosave guard skip the whole save (#896), silently
      // dropping the picked value.
      if (initialPublicRendering) {
        const emptyIndex = prev.findIndex(
          r => r.isNew && !r.publicRendering.trim() && !r.internalRendering.trim()
        );
        if (emptyIndex !== -1) {
          return prev.map((r, i) =>
            i === emptyIndex ? { ...r, publicRendering: initialPublicRendering } : r
          );
        }
      }
      const newRecord: EditableProvenanceRecord = {
        id: crypto.randomUUID(),
        publicRendering: initialPublicRendering ?? '',
        internalRendering: '',
        isPublic: false,
        isNew: true,
      };
      return [...prev, newRecord];
    });
  }, []);

  /**
   * Update a field on any record (new or existing).
   */
  const updateRecord = useCallback((
    id: string,
    field: 'publicRendering' | 'internalRendering' | 'isPublic',
    value: string | boolean
  ) => {
    setRecords(prev => prev.map(r =>
      r.id === id ? { ...r, [field]: value } : r
    ));
  }, []);

  /**
   * Delete a record from the list.
   */
  const deleteRecord = useCallback((id: string) => {
    setRecords(prev => prev.filter(r => r.id !== id));
  }, []);

  /**
   * Reorder records via drag-and-drop.
   */
  const reorderRecords = useCallback((fromIndex: number, toIndex: number) => {
    setRecords(prev => {
      const result = [...prev];
      const [removed] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, removed);
      return result;
    });
  }, []);

  /**
   * Save all records to the server.
   * Uses PUT endpoint which replaces all records atomically.
   * Includes version for optimistic locking to prevent concurrent edit data loss.
   * Returns true on success, false on failure.
   *
   * Note: This function preserves local edits made during the API call by merging
   * server response with current local state instead of blindly overwriting.
   */
  const saveRecords = useCallback(async (id?: string): Promise<boolean> => {
    const itemId = id || catalogItemId;
    if (!itemId) throw new Error('No catalog item ID provided');

    // Filter out completely empty records
    const validRecords = records.filter(
      r => r.publicRendering.trim() || r.internalRendering.trim()
    );

    setIsSaving(true);
    setSaveError(null);
    setHasConflict(false);
    try {
      const recordsToSave = validRecords.map((record, index) => ({
        recordId: record.recordId, // undefined for new records
        publicRendering: record.publicRendering,
        internalRendering: record.internalRendering,
        order: index,
        isPublic: record.isPublic,
      }));

      // Include expectedVersion for optimistic locking
      const response = await replaceProvenanceRecords(itemId, recordsToSave, versionRef.current, ownerId);

      // Update version from response
      versionRef.current = (response as { version?: number }).version ?? versionRef.current + 1;

      // Merge server response with current local state to preserve edits made during the API call
      // Only update record IDs (for new records) and isNew status from server
      setRecords(currentRecords => {
        const serverRecords = response.records as ProvenanceRecord[];

        // If no local records or counts don't match, use server records directly
        if (currentRecords.length === 0 || serverRecords.length !== currentRecords.length) {
          return serverRecords.map(r => ({
            id: r.recordId,
            recordId: r.recordId,
            publicRendering: r.publicRendering,
            internalRendering: r.internalRendering,
            isPublic: r.isPublic,
            isNew: false,
          }));
        }

        // Merge: preserve local edits, update recordId for new records
        return currentRecords.map((localRecord, index) => {
          const serverRecord = serverRecords[index];
          if (!serverRecord) return localRecord;

          // If this was a new record, it now has a server recordId
          // Preserve local edits but update the ID
          return {
            ...localRecord,
            id: serverRecord.recordId,
            recordId: serverRecord.recordId,
            isNew: false,
          };
        });
      });

      return true;
    } catch (err: unknown) {
      logger.error('Failed to save provenance records', err);

      // Check for 409 Conflict (concurrent modification)
      const error = err as { response?: { status?: number }; message?: string };
      if (error.response?.status === 409) {
        setHasConflict(true);
        setSaveError('Another user modified these records. Please reload to see the latest changes.');
      } else {
        setSaveError(error.message || 'Failed to save provenance records');
      }

      return false;
    } finally {
      setIsSaving(false);
    }
  }, [catalogItemId, records, replaceProvenanceRecords, ownerId]);

  /**
   * Clear all records (local state only, doesn't save).
   */
  const clearRecords = useCallback(() => {
    setRecords([]);
  }, []);

  /**
   * Check if there are unsaved changes.
   */
  const hasUnsavedChanges = useCallback(() => {
    return records.some(r => r.isNew);
  }, [records]);

  /**
   * Clear error state (useful after user acknowledges the error).
   */
  const clearError = useCallback(() => {
    setSaveError(null);
    setHasConflict(false);
  }, []);

  /**
   * Reload records after a conflict (fetches latest from server).
   */
  const reloadAfterConflict = useCallback(async () => {
    if (!catalogItemId && !loadedCatalogIdRef.current) return;
    const itemId = loadedCatalogIdRef.current || catalogItemId;
    if (itemId) {
      await loadProvenanceChain(itemId);
    }
  }, [catalogItemId, loadProvenanceChain]);

  return {
    // State
    records,
    isSaving,
    isLoading: localLoading,
    error,
    saveError,
    hasConflict,

    // Actions
    loadProvenanceChain,
    addRecord,
    updateRecord,
    deleteRecord,
    reorderRecords,
    saveRecords,
    clearRecords,
    hasUnsavedChanges,
    clearError,
    reloadAfterConflict,
  };
};
